import React, { useEffect, useState, useRef } from "react";
import clsx from "clsx";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import Layout from "../layouts";
import { useAccountStore } from "../store";
import axios, { apiBaseUrl } from "../services/http";
import BookingForm from "../components/BookingForm";
import { ToastContainer, toast } from "react-toastify";
import Slider from "react-slick";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from "@headlessui/react";
import { Tooltip } from "react-tooltip";

function TimeslotArrowNext(props) {
  const { onClick, className } = props;
  return (
    <button
      onClick={onClick}
      className={clsx(
        "ts-next rounded-full lg:px-2 lg:py-2 px-1 py-1",
        className.includes("-disabled")
          ? "bg-gray-100 opacity-50 pointer-events-none"
          : "bg-orange-100"
      )}
      disabled={className.includes("-disabled")}
    >
      <svg
        className="lg:w-4 lg:h-4 w-3 h-3"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M8.25 4.5l7.5 7.5-7.5 7.5"
        ></path>
      </svg>
    </button>
  );
}

function TimeslotArrowPrev(props) {
  const { onClick, className } = props;
  return (
    <button
      onClick={onClick}
      className={clsx(
        "ts-prev rounded-full lg:px-2 lg:py-2 px-1 py-1",
        className.includes("-disabled")
          ? "bg-gray-100 opacity-50 pointer-events-none"
          : "bg-orange-100"
      )}
      disabled={className.includes("-disabled")}
    >
      <svg
        className="lg:w-4 lg:h-4 w-3 h-3"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M15.75 19.5L8.25 12l7.5-7.5"
        ></path>
      </svg>
    </button>
  );
}

export default function Space() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {me} = useAccountStore();
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeslot, setSelectedTimeslot] = useState(null);
  const [space, setSpace] = useState(null);
  const [error, setError] = useState(null);
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [datePage, setDatePage] = useState(0);
  const tsSlider = useRef(null);
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    const fetchAvailability = async () => {
      setLoading(true);
      const date = selectedDate ? selectedDate.replace(/-/g, "") : "";
      const tsStart = selectedTimeslot?.value?.[0].replace(/[\s\-:]/g, "") || "";
      const tsEnd = selectedTimeslot?.value?.[1].replace(/[\s\-:]/g, "") || "";
      try {
        toast.dismiss();
        const response = await axios.get(
          `${apiBaseUrl}/api/spaces/${id}?date=${date}&ts_start=${tsStart}&ts_end=${tsEnd}`
        );
        setError(null);
        setSpace(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message);
        toast.error(err.response?.data?.message);
        // toast.error(
        //   "No sessions available for booking. Please try with a different timeslot."
        // );
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [id, selectedDate, selectedTimeslot]);

  useEffect(() => {
    if (space?.timeslots) {
      const index = space.timeslots.findIndex(ts => ts.enabled);
      tsSlider.current.slickGoTo(index);
    }
  }, [space?.timeslots]);

  const onDateChange = (date) => {
    setSelectedDate(date);
    setSelectedTimeslot(null);
  };

  const onRoomClick = async (room) => {
    if (room.status.value !== "available") {
      return;
    }

    if (!me) {
      return navigate('/login', {
        state: {
          from: location,
        }
      });
    }

    const date = selectedDate ? selectedDate.replace(/-/g, "") : space.filter.date.replace(/-/g, '');
    const tsStart = selectedTimeslot?.value?.[0].replace(/[\s\-:]/g, "") || space.filter.timeslot?.[0].replace(/[\s\-:]/g, "");
    const tsEnd = selectedTimeslot?.value?.[1].replace(/[\s\-:]/g, "") || space.filter.timeslot?.[1].replace(/[\s\-:]/g, "");
    try {
      toast.dismiss();
      const response = await axios.get(
        `${apiBaseUrl}/api/rooms/${room.id}?date=${date}&ts_start=${tsStart}&ts_end=${tsEnd}`
      );
      setRoom(response.data);
    } catch (error) {
      toast.error(error.response.data.message);
    }
  };

  const onDateNav = (direction) => {
    if (direction === -1) {
      setDatePage(datePage - 1);
    } else {
      setDatePage(datePage + 1);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto">
        <div className="lg:py-5 lg:px-0 py-4 px-4">
          {space && (
            <nav className="flex mb-4" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3 flex-wrap">
                <li className="inline-flex items-center lg:mb-0 mb-1">
                  <Link
                    to="/"
                    className="lg:text-sm text-xs font-medium text-orange-600"
                  >
                    Library Booking System
                  </Link>
                </li>
                <li className="lg:mb-0 mb-1">
                  <div className="flex items-center">
                    <svg
                      aria-hidden="true"
                      className="lg:w-6 lg:h-6 w-4 h-4 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                    <Link
                      to="/"
                      className="lg:text-sm text-xs font-medium text-orange-600 lg:ml-2 ml-1"
                    >
                      Rooms / Spaces
                    </Link>
                  </div>
                </li>
                <li aria-current="page" className="lg:mb-0 mb-1">
                  <div className="flex items-center">
                    <svg
                      aria-hidden="true"
                      className="lg:w-6 lg:h-6 w-4 h-4 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                    <span className="lg:text-sm text-xs font-medium text-gray-500 lg:ml-2 ml-1">
                      {space && space.name}
                    </span>
                  </div>
                </li>
              </ol>
            </nav>
          )}
          {!space && loading && (
            <div className="text-center mt-10">
              <div role="status">
                <svg
                  aria-hidden="true"
                  className="inline w-8 h-8 mr-2 text-gray-200 animate-spin fill-orange-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span class="sr-only">Loading...</span>
              </div>
            </div>
          )}
          {error && (
            <>
              <div className="mt-10 w-full max-w-lg mx-auto flex flex-col items-center lg:space-y-5 space-y-2">
                <svg
                  className="w-20 h-20 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                  ></path>
                </svg>
                <h3 className="font-bold lg:text-lg text-center">Error</h3>
                <p className="text-gray-500 text-center">{error}</p>
              </div>
            </>
          )}
          {space && (
            <div className="space-y-8">
              <div className="flex flex-col items-center justify-center mb-4">
                <h3 className="font-bold text-xl mb-4">
                  {space.calendar.header}
                </h3>
                <div className="flex items-center lg:space-x-4 space-x-1">
                  {space.calendar.selectable_dates.length > 7 && (
                    <button
                      onClick={() => onDateNav(-1)}
                      className={clsx(
                        "rounded-full lg:px-2 lg:py-2 px-1 py-1",
                        datePage === 0
                          ? "bg-gray-100 opacity-50 pointer-events-none"
                          : "bg-orange-100"
                      )}
                      disabled={datePage === 0}
                    >
                      <svg
                        className="lg:w-4 lg:h-4 w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M15.75 19.5L8.25 12l7.5-7.5"
                        ></path>
                      </svg>
                    </button>
                  )}
                  {space.calendar.selectable_dates
                    .slice(datePage * 7, datePage * 7 + 7)
                    .map((date, i) =>
                      date.enabled ? (
                        <button
                          key={i}
                          className={clsx(
                            "rounded-full lg:w-16 lg:h-16 w-12 h-12 lg:px-3 lg:py-3 px-1 py-1 flex items-center justify-center flex-col",
                            date.full === space.filter.date
                              ? "bg-orange-600 text-white"
                              : "bg-orange-100 hover:bg-orange-200"
                          )}
                          onClick={() => onDateChange(date.full)}
                        >
                          <span className="lg:text-sm text-xs">{date.dow}</span>
                          <p className="font-bold lg:text-lg">{date.dom}</p>
                        </button>
                      ) : (
                        <button
                          key={i}
                          disabled={true}
                          className={clsx(
                            "rounded-full lg:w-16 lg:h-16 w-12 h-12 lg:px-3 lg:py-3 px-1 py-1 flex items-center justify-center flex-col bg-gray-100 text-gray-400"
                          )}
                          onClick={() => onDateChange(date.full)}
                        >
                          <span className="lg:text-sm text-xs">{date.dow}</span>
                          <p className="font-bold lg:text-lg">{date.dom}</p>
                        </button>
                      )
                    )}
                  {space.calendar.selectable_dates.length > 7 && (
                    <button
                      onClick={() => onDateNav(1)}
                      className={clsx(
                        "rounded-full lg:px-2 lg:py-2 px-1 py-1",
                        datePage ===
                          Math.floor(
                            space.calendar.selectable_dates.length / 7
                          ) -
                            1
                          ? "bg-gray-100 opacity-50 pointer-events-none"
                          : "bg-orange-100 hover:bg-orange-200"
                      )}
                      disabled={
                        datePage ===
                        Math.floor(space.calendar.selectable_dates.length / 7) -
                          1
                      }
                    >
                      <svg
                        className="lg:w-4 lg:h-4 w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M8.25 4.5l7.5 7.5-7.5 7.5"
                        ></path>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="slider-container lg:px-0 px-2">
                <Slider
                  ref={tsSlider}
                  dots={false}
                  infinite={false}
                  speed={500}
                  arrows={space.timeslots.length > 12}
                  slidesToShow={
                    space.timeslots.length > 12 ? 12 : space.timeslots.length
                  }
                  slidesToScroll={12}
                  draggable={space.timeslots.length > 12}
                  nextArrow={<TimeslotArrowNext />}
                  prevArrow={<TimeslotArrowPrev />}
                  responsive={[
                    {
                      breakpoint: 1024,
                      settings: {
                        slidesToShow: 3,
                        slidesToScroll: 3,
                        arrows: true,
                      },
                    },
                  ]}
                >
                  {space.timeslots.map((timeslot, i) => (
                    <div key={i}>
                      <button
                        key={i}
                        disabled={!timeslot.enabled}
                        onClick={() => setSelectedTimeslot(timeslot)}
                        className={clsx(
                          "relative w-full flex-1 rounded-lg border lg:px-4 lg:py-3 px-4 py-2 disabled:pointer-events-none disabled:text-gray-300",
                          timeslot.value[0] === space.filter.timeslot?.[0]
                            ? "bg-orange-600 text-white border-orange-600 font-bold"
                            : ""
                        )}
                      >
                        {timeslot.desc}
                      </button>
                      {timeslot.enabled && timeslot.is_free_seating && (
                        <p className="text-xs text-yellow-700 text-center mt-1">
                          Free Seating
                        </p>
                      )}
                    </div>
                  ))}
                </Slider>
              </div>

              {space.availability.length > 0 ? (
                <div>
                  <div className="mb-4 flex items-center space-x-2">
                    <h3 className="font-bold text-xl">
                      {space.space.floor.name} {space.name}
                    </h3>
                    <a
                      className="text-orange-600"
                      href={space.space.info_url}
                      rel="noreferrer"
                      target="_blank"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                        ></path>
                      </svg>
                    </a>
                  </div>
                  <div className="flex items-center justify-between lg:mb-4 mb-8">
                    <ul className="flex items-center flex-wrap">
                      <li className="flex items-center space-x-1 lg:mr-4 mr-2">
                        <span className="inline-block w-4 h-4 bg-red-600"></span>
                        <span className="text-sm">In Use</span>
                      </li>
                      <li className="flex items-center space-x-1 lg:mr-4 mr-2">
                        <span className="inline-block w-4 h-4 bg-[#fbe152]"></span>
                        <span className="text-sm">Reserved</span>
                      </li>
                      <li className="flex items-center space-x-1 lg:mr-4 mr-2">
                        <span className="inline-block w-4 h-4 bg-black"></span>
                        <span className="text-sm">Not Available</span>
                      </li>
                      <li className="flex items-center space-x-1">
                        <span className="inline-block w-4 h-4 bg-[#99efae]"></span>
                        <span className="text-sm">Available</span>
                      </li>
                    </ul>
                  </div>

                  <div className="relative">
                    {loading && (
                      <div className="absolute top-0 left-0 right-0 bottom-0 bg-white/50 flex justify-center">
                        <svg
                          aria-hidden="true"
                          className="inline-block w-8 h-8 text-gray-200 animate-spin fill-orange-600"
                          viewBox="0 0 100 101"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                          />
                          <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill"
                          />
                        </svg>
                      </div>
                    )}
                    <div className="lg:block hidden">
                      <Tooltip id="booking-tooltip" />
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox={`0 0 ${space.map.width} ${space.map.height}`}
                        className="space-map"
                      >
                        <image
                          width={space.map.width}
                          height={space.map.height}
                          href={space.map.url}
                        ></image>
                        {space.availability.map((room, i) => (
                          <>
                            <g
                              key={i}
                              onClick={() => onRoomClick(room)}
                              className={clsx(
                                room.status.value === "available"
                                  ? "cursor-pointer"
                                  : "not-available"
                              )}
                              data-tooltip-id="booking-tooltip"
                              data-tooltip-content={room.status.booker_info}
                            >
                              {room.map_props.type === "rectangle" ? (
                                <>
                                  <rect
                                    x={room.map_props.x}
                                    y={room.map_props.y}
                                    fill={room.status.style.bg}
                                    width={room.map_props.w}
                                    height={room.map_props.h}
                                    style={
                                      room.map_props.r !== 0
                                        ? {
                                            transformBox: "fill-box",
                                            transformOrigin: "center",
                                            rotate: room.map_props.rt + "deg",
                                          }
                                        : {}
                                    }
                                  />
                                  <text
                                    x={room.map_props.x + room.map_props.w / 2}
                                    y={room.map_props.y + room.map_props.h / 2}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fill={room.status.style.fg}
                                    style={{ fontWeight: 600 }}
                                  >
                                    {room.name}
                                  </text>
                                </>
                              ) : (
                                <>
                                  <circle
                                    cx={room.map_props.x}
                                    cy={room.map_props.y}
                                    r={room.map_props.r}
                                    fill={room.status.style.bg}
                                  />
                                  <text
                                    x={room.map_props.x}
                                    y={
                                      room.map_props.y +
                                      (room.map_props.label_pos === "center"
                                        ? 0
                                        : room.map_props.label_pos === "above"
                                        ? -1 * room.map_props.r - 10
                                        : room.map_props.r + 10)
                                    }
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    lengthAdjust="spacingAndGlyphs"
                                    fill={
                                      room.map_props.label_pos === "center"
                                        ? room.status.style.fg
                                        : "#000"
                                    }
                                    style={
                                      room.map_props.label_pos !== "center"
                                        ? {
                                            fontWeight: 600,
                                            paintOrder: "stroke",
                                            stroke: "#fff",
                                            strokeWidth: 3,
                                            fontSize: 11,
                                          }
                                        : { fontWeight: 600 }
                                    }
                                  >
                                    {room.name}
                                  </text>
                                </>
                              )}
                            </g>
                          </>
                        ))}
                      </svg>
                    </div>
                    <div className="lg:hidden block">
                      <div className="grid grid-cols-4 gap-4">
                        {space.availability.map((room, i) => (
                          <button
                            key={i}
                            onClick={() => onRoomClick(room)}
                            className="rounded border px-4 py-2 text-sm"
                            style={{
                              backgroundColor:
                                room.status.style.bg === "none"
                                  ? "#fff"
                                  : room.status.style.bg,
                              color: room.status.style.fg,
                            }}
                          >
                            {room.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-full max-w-lg mx-auto flex flex-col items-center lg:space-y-5 space-y-2">
                    <svg
                      className="w-20 h-20 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                      ></path>
                    </svg>
                    <h3 className="font-bold lg:text-lg text-center">
                      There are currently no rooms available for booking
                    </h3>
                    <p className="text-gray-500">
                      Please try a different date.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {room && (
        <BookingForm room={room} onUpdateTimeslot={setSelectedTimeslot} />
      )}
      <ToastContainer
        position="bottom-right"
        hideProgressBar={true}
        closeButton={false}
        limit={1}
      />

      <Dialog
        open={infoOpen}
        as="div"
        className="relative z-10 focus:outline-none"
        onClose={() => setInfoOpen(false)}
      >
        <DialogBackdrop className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {space && (
              <DialogPanel
                transition
                className="w-full max-w-md rounded-xl bg-white p-6 backdrop-blur-2xl duration-200 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0"
              >
                <DialogTitle as="h3" className="text-base/7 mb-3">
                  {space.space.floor.name} {space.name}
                </DialogTitle>
                <ul className="list-disc ml-5">
                  <li>
                    <b>Session length:</b> {space.session_length.desc}
                  </li>
                  <li>
                    <b>Minimum user(s) required:</b> {space.space.min_users}
                  </li>
                  <li>
                    <b>Time to check in:</b> {space.space.checkin_mins} minutes
                    after session begins
                  </li>
                  <li>
                    <b>Restrictions:</b> {space.space.restrictions}
                  </li>
                </ul>
                {space.space.info_url && (
                  <p className="mt-2">
                    Please visit the{" "}
                    <a
                      href={space.space.info_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-orange-600 hover:underline"
                    >
                      website
                    </a>{" "}
                    for more information on this space.
                  </p>
                )}
                <div className="mt-4">
                  <button
                    className="text-white font-semibold bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 rounded-lg px-4 py-2 text-center"
                    onClick={() => setInfoOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </DialogPanel>
            )}
          </div>
        </div>
      </Dialog>
    </Layout>
  );
}
